import { getAmplitudeInstance } from '../amplitudeHelpers';
import CardType from '../enums/CardType';
import ExploreUGCBadge from '../enums/ExploreUGCBadge';
import ExploreViewType from '../enums/ExploreViewType';
import FilterName from '../enums/FilterName';
import LocationType from '../enums/LocationType';
import MapBaseLayer from '../enums/MapBaseLayer';
import MapOverlay from '../enums/MapOverlay';

type logExploreCardViewedParams = {
  bearing?: number;
  card_id: number;
  card_index: number;
  card_type: CardType;
  collections_applied?: string[];
  collections_available?: string[];
  coordinates?: number[];
  explore_view_type: ExploreViewType;
  filters_applied?: FilterName[];
  location_id?: number;
  location_type?: LocationType;
  map_base_layer?: MapBaseLayer;
  map_overlays?: MapOverlay[];
  num_results_returned: number;
  photo_id?: number;
  pitch?: number;
  ugc_badge_count?: number;
  ugc_badge_shown?: ExploreUGCBadge;
  zoom_level?: number;
};

/**
 * Track when a user views an explore card on screen. Trigger an event each time a trail card or mini card comes partially into view for the user in order to understand how deep users are scrolling in their list-view or how engaged they are with pins in the map-view, regardless of clicking on the card.
 * @param params - Attributes to log with the amplitude event
 * @param params.bearing - Numeric degrees indicating the bearing.
 * @param params.card_id - Unique identifier of trail viewed
 * @param params.card_index - The 0-indexed position of the card viewed, depending on explore view type. Represents the vertical position of the card when the user views a card from the list-view. Represents the horizontal position of the card when the user views on a mini-card from cards grouped at a trailhead. Pass nil when a user clicks on a mini-card that is not grouped at the trailhead.
 * @param params.card_type -  Describes the type of card viewed. Always pass ‘trail’ until we start pulling in other card types.
 * @param params.collections_applied - The name for the collections that were set when an action took place.
 * @param params.collections_available - List of strings containing the collections available to the user.
 * @param params.coordinates - Float array of center
 * @param params.explore_view_type - Describes the type of explore view the user was in when the action took place (e.g., ‘list’,  ‘map’ or ‘split’)
 * @param params.filters_applied - List of strings containing the filters applied by the user
 * @param params.location_id - Unique identifier of the location that was set at the time the action took place
 * @param params.location_type - Type of location set (e.g.  ‘city’, ‘park’, ‘map area’, the pois  etc.) Note: All locations correspond to pois, with the exception of map area. The ‘map area’ location type is set when the user freely pans in the open map mode and the ‘map area’ term shows up in the search box.
 * @param params.map_base_layer - Name of tile base layer currently selected
 * @param params.map_overlays - The overlays currently applied to the map.
 * @param params.num_results_returned - Total number of trails returned
 * @param params.photo_id - The photo id of the photo present on the card at the time of viewing.
 * @param params.pitch - Numeric value indicating the tilt.
 * @param params.ugc_badge_count - The number of shown in the UGC badge. For example, if the badge shows '7 reviews', ugc_badge_count would be 7. If no UGC badge is on the explore card, null value
 * @param params.ugc_badge_shown - The type of UGC shown in the UGC badge. For example, if the badge shows '7 reviews', ugc_badge_shown would be review. If no UGC badge is on the explore card, null value
 * @param params.zoom_level -  Numeric value indicating the zoom level.
 */
const logExploreCardViewed = (
  {
    bearing,
    card_id,
    card_index,
    card_type,
    collections_applied,
    collections_available,
    coordinates,
    explore_view_type,
    filters_applied,
    location_id,
    location_type,
    map_base_layer,
    map_overlays,
    num_results_returned,
    photo_id,
    pitch,
    ugc_badge_count,
    ugc_badge_shown,
    zoom_level
  }: logExploreCardViewedParams,
  callback?: () => void
) => {
  getAmplitudeInstance().logEvent(
    'explore card viewed',
    {
      bearing,
      'card id': card_id,
      'card index': card_index,
      'card type': card_type,
      'collections applied': collections_applied,
      'collections available': collections_available,
      coordinates,
      'explore view type': explore_view_type,
      'filters applied': filters_applied,
      'location id': location_id,
      'location type': location_type,
      'map base layer': map_base_layer,
      'map overlays': map_overlays,
      'num results returned': num_results_returned,
      'photo id': photo_id,
      pitch,
      'ugc badge count': ugc_badge_count,
      'ugc badge shown': ugc_badge_shown,
      'zoom level': zoom_level
    },
    callback
  );
};

export default logExploreCardViewed;
